import { useRef, useState, useCallback } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { Reveal, ChapterLabel } from "./Reveal";

export default function Experience() {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const update = useCallback((clientX) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => dragging.current && update(e.clientX);
  const stop = () => (dragging.current = false);

  return (
    <section id="experience" data-testid="experience-section" className="relative py-16 sm:py-24">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <ChapterLabel number="03" label="The Transformation" />
        <Reveal delay={0.1}>
          <h2
            data-testid="experience-heading"
            className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#FFFDF9] max-w-2xl"
          >
            Same classroom.
            <br />A whole new way to{" "}
            <span className="italic text-[#38BDF8]">understand</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-4 max-w-md text-sm text-slate-400 leading-relaxed">
            Drag the divider. Nothing about the room changes — except what becomes visible.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-10">
          <div
            ref={ref}
            data-testid="before-after-slider"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={stop}
            onPointerCancel={stop}
            style={{ touchAction: "pan-y" }}
            className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl select-none cursor-ew-resize border border-white/[0.08]"
          >
            <img
              src="/images/classroom-before.jpg"
              alt="Classroom without AuraXR"
              draggable={false}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
              <img
                src="/images/classroom-after.jpg"
                alt="Classroom with AuraXR"
                draggable={false}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>

            <span
              data-testid="label-without-auraxr"
              className="absolute left-5 top-5 rounded-full bg-black/55 px-4 py-1.5 text-[10px] tracking-[0.28em] uppercase text-slate-200 backdrop-blur-sm"
            >
              Without AuraXR
            </span>
            <span
              data-testid="label-with-auraxr"
              className="absolute right-5 top-5 rounded-full bg-[#38BDF8]/90 px-4 py-1.5 text-[10px] tracking-[0.28em] uppercase text-[#050811] font-semibold backdrop-blur-sm"
            >
              With AuraXR
            </span>

            <div
              data-testid="slider-divider"
              className="absolute top-0 bottom-0 w-px bg-white/80"
              style={{ left: `${pos}%` }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-[#FFFDF9] text-[#050811] shadow-[0_8px_30px_rgba(0,0,0,0.45)] transition-transform duration-300 hover:scale-110">
                <ChevronsLeftRight size={18} />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
